import { createEffect, createSignal, onCleanup, onMount } from "solid-js";
import { msg } from "@lingui/core/macro";

import type { Todo } from "@listto/powersync";
import {
  checkUpcomingReminders,
  getUpcomingRemindersQuery,
  MAX_SCHEDULED_REMINDERS,
  queryKeys,
  syncReminders,
} from "@listto/shared";
import { createPowerSyncQuery } from "@listto/shared/solid";

import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useI18n } from "@/providers/i18n-provider";
import { OFFLINE_CACHE_STALE_TIME_MS, ONE_HOUR_MS } from "@/lib/constants";
import {
  getNotificationAdapter,
  type NotificationAdapter,
  type ReminderContent,
} from "@/lib/notifications/service";

const FOREGROUND_POLL_MS = 30_000;
const WEB_REMINDER_LIMIT = 20;

/**
 * Drives todo reminders across every platform served by this bundle.
 *
 *   - Tauri (desktop + mobile): registers each upcoming todo with the OS
 *     scheduler so reminders fire even when the app is closed. Re-syncs
 *     (diffing against what's already scheduled) whenever the todo set changes.
 *   - Web / PWA: no OS scheduler available, so it polls in the foreground and
 *     fires a notification the moment a due date is reached while a window is
 *     open.
 *
 * Mount once (see `ReminderScheduler`), inside the PowerSync provider.
 */
export function useTodoReminders(): void {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { t } = useI18n();

  // Regla 3: reminderContent runs imperatively (not rendered) but the hook has
  // context access, so we use t() for reactivity at the moment of invocation.
  const reminderContent = (todo: Todo): ReminderContent => ({
    title: t(msg`Task Reminder`),
    body: todo.text ?? t(msg`Your tasks are waiting`),
  });

  const [adapter, setAdapter] = createSignal<NotificationAdapter | null>(null);
  const notifiedIds = new Set<string>();
  let syncing = false;

  onMount(() => {
    void (async () => {
      const resolved = await getNotificationAdapter();
      setAdapter(resolved);
      // Best-effort early prompt. On web this only prompts with prior user
      // activation; otherwise it silently resolves and we prompt again lazily.
      await resolved.requestPermission();
    })();
  });

  // Single reactive pool of upcoming todos with a due date. The window is wide
  // enough for OS scheduling (≤ MAX_SCHEDULED_REMINDERS) and is further
  // narrowed for the web foreground path by the time-window check.
  const query = createPowerSyncQuery<Todo>({
    queryKey: () => queryKeys.todos.remindersPool(),
    db: () => db(),
    queryFn: () => {
      const d = drizzle();
      if (!d) {
        return null;
      }
      const adapterRef = adapter();
      const scheduling = adapterRef?.supportsScheduling ?? false;
      return getUpcomingRemindersQuery(d, {
        limit: scheduling ? MAX_SCHEDULED_REMINDERS : WEB_REMINDER_LIMIT,
        // Web only cares about the near term; Tauri schedules the full pool.
        maxDueDate: scheduling ? undefined : new Date(Date.now() + ONE_HOUR_MS).toISOString(),
      });
    },
    enabled: () => Boolean(db()) && Boolean(drizzle()) && Boolean(adapter()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    throttleTime: 1000,
  });

  const syncScheduled = async (resolved: NotificationAdapter, todos: Todo[]) => {
    if (syncing) {
      return;
    }
    syncing = true;
    try {
      if (!(await resolved.requestPermission())) {
        return;
      }
      const currentlyScheduled = await resolved.getScheduled();
      await syncReminders({
        todos,
        currentlyScheduled,
        schedule: async (todo) => resolved.schedule(todo, reminderContent(todo)),
        cancel: async (id) => resolved.cancel(id),
        maxNotifications: MAX_SCHEDULED_REMINDERS,
      });
    } catch (error) {
      console.error("[notifications] Failed to sync reminders:", error);
    } finally {
      syncing = false;
    }
  };

  const checkForeground = (resolved: NotificationAdapter, todos: Todo[]) => {
    checkUpcomingReminders({
      todos,
      notifiedIds,
      onNotify: (todo) => {
        const content = reminderContent(todo);
        void resolved.show(content.title, { body: content.body, data: { todoId: todo.id } });
      },
    });
  };

  // React to data changes: Tauri re-syncs the OS schedule; web re-checks now.
  createEffect(() => {
    const resolved = adapter();
    const todos = query.data ?? [];
    if (!resolved) {
      return;
    }
    if (resolved.supportsScheduling) {
      void syncScheduled(resolved, todos);
    } else {
      checkForeground(resolved, todos);
    }
  });

  // Foreground heartbeat for the web path: detect due dates crossing "now"
  // even when the underlying data hasn't changed.
  onMount(() => {
    const interval = setInterval(() => {
      const resolved = adapter();
      if (resolved && !resolved.supportsScheduling) {
        checkForeground(resolved, query.data ?? []);
      }
    }, FOREGROUND_POLL_MS);
    onCleanup(() => {
      clearInterval(interval);
    });
  });
}
